import { FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { ERROR_CLASSNAME } from 'common/constants/messages/validations';
import { largeCurrencyFormat } from 'common/formats/formats';
import { getEquityValueFormula } from 'common/formulas/math.js/ENTERPRISE_AND_EQUITY_VALUE';
import { UnitAwareInputPolicy } from 'components/Spreadsheet/utilities';
import withTooltip from 'components/WithTooltip/withTooltip';
import {
  ConfigurationCellMap,
  ICell,
  ParserParams,
} from 'models/interfaces/spreadsheet-types/core-types';
import {
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  LESS_DEBT_ALIAS,
  PLUS_CASH_ALIAS,
  TABLE_HEADER_CLASS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import { INPUT_SHEET_HEADER_ALIAS } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/constants';
import InputSheetParser from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/InputSheetParser/InputSheetParser';
import { inputSheetAfterCellsChanged } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/InputSheetRowConfig/inputSheetAfterCellsChanged';
import { InputSheetRow } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/InputSheetRowConfig/inputSheetRowConfig.props';
import WaterfallFXValueViewer from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXValueViewer/WaterfallFXValueViewer';
import palette from 'theme/palette';
import { addClassName, removeClassName } from 'utilities';
import {
  SpreadsheetConfig,
  SpreadsheetConfigConstructorParams,
} from '../../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';

const RELATED_ENTITY_INPUT_SHEET_PREFIX = 'RelatedEntityInput';
const RELATED_ENTITY_INPUT_SHEET_PATTERN = new RegExp(`^${RELATED_ENTITY_INPUT_SHEET_PREFIX}_(\\d+)$`);
const EQUITY_VALUE_FORMULA_TOOLTIP = 'Enterprise Value plus Cash less Debt.';
const RelatedEntityEquityValueViewer = withTooltip(WaterfallFXValueViewer);

interface RelatedEntityInputColumn {
  id?: string | number;
}

export type RelatedEntityInputSheet = SpreadsheetConfig<object, RelatedEntityInputColumn, object, object>;

export const getRelatedEntityInputSheetName = (companyId: number) =>
  `${RELATED_ENTITY_INPUT_SHEET_PREFIX}_${companyId}`;

export const applyRelatedEntityEquityFallback = (equityValueCell: ICell, equityValue: number, currencyCode?: string) => {
  const isDefaultingToZero = Number.isFinite(equityValue) && equityValue < 0;
  const zero = `${currencyCode || 'USD'} 0`;
  // Reuse the full spreadsheet error appearance without invalidating the valid zero calculation.
  const nextClassName = isDefaultingToZero
    ? addClassName(equityValueCell.className ?? '', ERROR_CLASSNAME)
    : removeClassName(equityValueCell.className ?? '', ERROR_CLASSNAME);
  const nextTooltipMessages = isDefaultingToZero
    ? [`Defaulting to ${zero} because the Related Entity's Exit Equity Value was less than ${zero}.`]
    : [EQUITY_VALUE_FORMULA_TOOLTIP];
  const hasChanged =
    equityValueCell.isValid !== true ||
    equityValueCell.className !== nextClassName ||
    equityValueCell.tooltipMessages?.[0] !== nextTooltipMessages[0];

  Object.assign(equityValueCell, {
    className: nextClassName,
    isValid: true,
    tooltipMessages: nextTooltipMessages,
  });

  if (hasChanged) {
    equityValueCell.dispatchEvent?.();
  }
};

export const getRelatedEntityIdFromInputSheetName = (sheetName: string): number | undefined => {
  const match = RELATED_ENTITY_INPUT_SHEET_PATTERN.exec(sheetName);
  if (!match) {
    return undefined;
  }
  const companyId = Number(match[1]);
  return Number.isSafeInteger(companyId) && companyId > 0 ? companyId : undefined;
};

const relatedEntityInputSheetParser = (
  params: ParserParams<RelatedEntityInputColumn, object, object, object>
): ConfigurationCellMap<object, object, RelatedEntityInputColumn, object> => {
  const cells = InputSheetParser(
    params as ParserParams<object, object, object, object>
  ) as ConfigurationCellMap<object, object, RelatedEntityInputColumn, object>;
  const findCell = (alias: string) => Object.values(cells).find(cell => cell.alias === alias);
  const enterpriseValueCell = findCell(EXIT_ENTERPRISE_VALUE_ALIAS);
  const plusCashCell = findCell(PLUS_CASH_ALIAS);
  const lessDebtCell = findCell(LESS_DEBT_ALIAS);
  const equityValueCell = findCell(EXIT_EQUITY_ALIAS);
  const foreignEquityValueCell = findCell(FUND_EXIT_EQUITY_ALIAS);
  const foreignHeaderCell = findCell(`${INPUT_SHEET_HEADER_ALIAS}_foreign`);
  const headerRow = (params.rowConfig as InputSheetRow[]).find(row => row.alias === INPUT_SHEET_HEADER_ALIAS);

  if (foreignHeaderCell) {
    foreignHeaderCell.value = headerRow?.foreignValue ?? '';
  }

  if (enterpriseValueCell?.key && plusCashCell?.key && lessDebtCell?.key && equityValueCell) {
    // Clamp only this related-entity sheet, not the shared equity formula.
    equityValueCell.expr = `=MAX(${getEquityValueFormula(
      enterpriseValueCell.key,
      plusCashCell.key,
      lessDebtCell.key
    )}, 0)`;

    const calculatedEquityValue =
      Number(enterpriseValueCell.value) + Number(plusCashCell.value) - Number(lessDebtCell.value);
    applyRelatedEntityEquityFallback(equityValueCell, calculatedEquityValue, String(headerRow?.value ?? ''));
    if (foreignEquityValueCell) {
      applyRelatedEntityEquityFallback(foreignEquityValueCell, calculatedEquityValue, String(headerRow?.foreignValue ?? ''));
      foreignEquityValueCell.tooltipPlacement = 'bottom';
      foreignEquityValueCell.valueViewer = RelatedEntityEquityValueViewer;
    }
  }

  Object.values(cells).forEach(cell => {
    // This compact input table is already visible. Enter should select the next
    // cell without re-centering the whole page through shared keyboard navigation.
    // eslint-disable-next-line no-param-reassign
    cell.ignoreAutoScroll = true;
    if ([EXIT_ENTERPRISE_VALUE_ALIAS, EXIT_EQUITY_ALIAS, FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS].includes(cell.alias ?? '')) {
      // eslint-disable-next-line no-param-reassign
      cell.readOnly = true;
    }
  });

  return cells;
};

interface CreateRelatedEntityInputSheetConfigParams {
  companyId: number;
  companyName: string;
  enterpriseValue: number;
  plusCash: number;
  lessDebt: number;
  isAdditionalCurrencySelected: boolean;
  exchangeRate: number | null;
  inputCurrencyCode?: string;
  inputPolicy: UnitAwareInputPolicy;
  outputCurrencyCode?: string;
}

export const createRelatedEntityInputSheetConfig = ({
  companyId,
  companyName,
  enterpriseValue,
  plusCash,
  lessDebt,
  isAdditionalCurrencySelected,
  exchangeRate,
  inputCurrencyCode,
  inputPolicy,
  outputCurrencyCode,
}: CreateRelatedEntityInputSheetConfigParams) => {
  const isExchangeRateReady = exchangeRate != null && Number.isFinite(exchangeRate) && exchangeRate > 0;
  const getFinancialInputFormat = (alias: string) => ({
    ...largeCurrencyFormat,
    inputScale: inputPolicy.getInputScale({ alias }),
  });
  const editableValueStyle = { color: palette.primary[700] };
  const rows: InputSheetRow[] = [
    {
      alias: INPUT_SHEET_HEADER_ALIAS,
      className: TABLE_HEADER_CLASS,
      foreignValue: isAdditionalCurrencySelected ? (outputCurrencyCode ?? '') : '',
      name: companyName,
      readOnly: true,
      titleAlias: `related_entity_${companyId}_header`,
      tooltipMessages: [],
      value: inputCurrencyCode ?? '',
    },
    {
      alias: EXIT_ENTERPRISE_VALUE_ALIAS,
      className: 'bold-row',
      foreignValue: isExchangeRateReady ? undefined : null,
      format: getFinancialInputFormat(EXIT_ENTERPRISE_VALUE_ALIAS),
      name: 'Exit Enterprise Value',
      readOnly: true,
      titleAlias: `related_entity_${companyId}_exit_enterprise_value_header`,
      tooltipMessages: ['Distribution received from the parent company.'],
      value: enterpriseValue,
    },
    {
      alias: PLUS_CASH_ALIAS,
      foreignValue: isExchangeRateReady ? undefined : null,
      format: getFinancialInputFormat(PLUS_CASH_ALIAS),
      name: 'Plus Cash',
      readOnly: false,
      style: editableValueStyle,
      titleAlias: `related_entity_${companyId}_plus_cash_header`,
      titleClassName: 'subtitle-indented',
      tooltipMessages: ['Cash held by this related entity at exit.'],
      value: plusCash,
    },
    {
      alias: LESS_DEBT_ALIAS,
      foreignValue: isExchangeRateReady ? undefined : null,
      format: getFinancialInputFormat(LESS_DEBT_ALIAS),
      name: 'Less Debt',
      readOnly: false,
      style: editableValueStyle,
      titleAlias: `related_entity_${companyId}_less_debt_header`,
      titleClassName: 'subtitle-indented',
      tooltipMessages: ['Debt held by this related entity at exit.'],
      value: lessDebt,
    },
    {
      alias: EXIT_EQUITY_ALIAS,
      className: 'bold-row',
      foreignValue: isExchangeRateReady ? undefined : null,
      format: getFinancialInputFormat(EXIT_EQUITY_ALIAS),
      name: 'Exit Equity Value',
      readOnly: true,
      titleAlias: `related_entity_${companyId}_exit_equity_value_header`,
      tooltipMessages: [EQUITY_VALUE_FORMULA_TOOLTIP],
      tooltipPlacement: 'bottom',
      value: 0,
    },
  ];

  return new SpreadsheetConfig({
    afterCellChanged: inputSheetAfterCellsChanged,
    colTitleRow: 0,
    columns: isAdditionalCurrencySelected ? [{}, {}, {}] : [{}, {}],
    name: getRelatedEntityInputSheetName(companyId),
    parser: relatedEntityInputSheetParser,
    reverseParser: undefined,
    rowConfig: rows as ICell<object, object, RelatedEntityInputColumn, object>[],
    showTitlesColumn: false,
    tableTerms: {},
  } as SpreadsheetConfigConstructorParams<object, RelatedEntityInputColumn, object, object>) as RelatedEntityInputSheet;
};
